import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { SignatureCanvas } from '../components/SignatureCanvas';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Loader2, Download, CheckCircle, FileSignature } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contract = () => {
  const { t } = useLanguage();
  const { contractId } = useParams();
  const contractRef = useRef(null);
  
  const [contract, setContract] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signature, setSignature] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const contractRes = await axios.get(`${API}/contracts/${contractId}`);
        setContract(contractRes.data);
        
        if (contractRes.data.client_id) {
          const clientRes = await axios.get(`${API}/clients/${contractRes.data.client_id}`);
          setClient(clientRes.data);
        }
      } catch (error) {
        console.error('Error fetching contract:', error);
        toast.error(t('error'));
      }
      setLoading(false);
    };
    fetchData();
  }, [contractId, t]);

  const handleSignatureCapture = (signatureData) => {
    setSignature(signatureData);
    toast.success('Signature enregistrée');
  };

  const handleSignContract = async () => {
    if (!signature) {
      toast.error('Veuillez signer le contrat');
      return;
    }

    setSigning(true);
    try {
      await axios.put(`${API}/contracts/${contractId}/sign`, { signature });
      setContract(prev => ({ ...prev, signature, status: 'signed', signed_at: new Date().toISOString() }));
      toast.success('Contrat signé avec succès !');
    } catch (error) {
      console.error('Error signing contract:', error);
      toast.error(t('error'));
    }
    setSigning(false);
  };

  const handleDownloadPDF = () => {
    // Create a simple printable version
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Contrat TANNOU - ${contractId}</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; }
          h1 { color: #0044CC; text-align: center; }
          .content { white-space: pre-wrap; line-height: 1.6; }
          .signature { margin-top: 40px; border-top: 1px solid #ccc; padding-top: 20px; }
          .signature img { max-width: 300px; }
          .terms { margin-top: 20px; padding: 15px; background: #f5f5f5; border-radius: 8px; }
          .terms h3 { margin-top: 0; }
          .terms ul { margin: 0; padding-left: 20px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>TANNOU</h1>
        <div class="content">${contract?.content || ''}</div>
        <div class="terms">
          <h3>Conditions particulières :</h3>
          <ul>
            ${contract?.terms?.map(term => `<li>${term}</li>`).join('') || ''}
          </ul>
        </div>
        ${contract?.signature ? `
          <div class="signature">
            <p><strong>Signature du client :</strong></p>
            <img src="${contract.signature}" alt="Signature" />
            <p>Signé le : ${new Date(contract.signed_at).toLocaleDateString('fr-FR')}</p>
          </div>
        ` : ''}
      </body>
      </html>
    `;

    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-slate-500">Contrat introuvable</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50" data-testid="contract-page">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileSignature className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-slate-900 mb-2">
            Contrat #{contractId.substring(0, 8).toUpperCase()}
          </h1>
          <div className="flex items-center justify-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              contract.status === 'signed'
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`}>
              {contract.status === 'signed' ? 'Signé' : 'En attente de signature'}
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Contract Preview */}
          <div className="lg:col-span-3">
            <Card className="shadow-lg border-0" data-testid="contract-preview-card">
              <CardHeader className="border-b">
                <CardTitle>Aperçu du contrat</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[600px] p-6">
                  <div 
                    ref={contractRef}
                    className="prose prose-slate max-w-none whitespace-pre-wrap font-mono text-sm"
                  >
                    {contract.content}
                  </div>
                  
                  {/* Terms */}
                  {contract.terms && contract.terms.length > 0 && (
                    <div className="mt-8 p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-semibold mb-3">Conditions particulières :</h4>
                      <ul className="space-y-2">
                        {contract.terms.map((term, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {term}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Existing Signature */}
                  {contract.signature && (
                    <div className="mt-8 p-4 border-t">
                      <h4 className="font-semibold mb-3">Signature du client :</h4>
                      <img 
                        src={contract.signature} 
                        alt="Signature" 
                        className="max-w-xs border rounded-lg"
                      />
                      <p className="text-sm text-slate-500 mt-2">
                        Signé le {new Date(contract.signed_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Signature Panel */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Informations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Client</span>
                  <span className="font-medium">{client?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Email</span>
                  <span className="font-medium">{client?.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Date</span>
                  <span className="font-medium">{new Date(contract.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
              </CardContent>
            </Card>

            {/* Signature Canvas */}
            {contract.status !== 'signed' && (
              <Card data-testid="signature-section">
                <CardHeader>
                  <CardTitle className="text-lg">{t('signature')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <SignatureCanvas onSave={handleSignatureCapture} />
                  
                  {signature && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-green-700 text-sm">Signature enregistrée</span>
                    </div>
                  )}
                  
                  <Button
                    onClick={handleSignContract}
                    disabled={signing || !signature}
                    className={`w-full mt-4 rounded-full ${
                      signature 
                        ? 'bg-primary hover:bg-primary-hover text-white' 
                        : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                    }`}
                    data-testid="sign-contract-btn"
                  >
                    {signing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        {signature ? t('signContract') : 'Signez d\'abord le contrat'}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Download Button */}
            <Button
              onClick={handleDownloadPDF}
              variant="outline"
              className="w-full rounded-full border-2"
              data-testid="download-contract-btn"
            >
              <Download className="w-5 h-5 mr-2" />
              {t('downloadContract')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
